import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { DisputeDto } from "@shared/types/DisputeDto";
import { DisputeProvider, useDisputes } from "@/providers/DisputeProvider";
import { DisputeStats } from "./DisputeStats";
import { DisputeFilters } from "./DisputeFilters";
import { DisputeTable } from "./DisputeTable";
import { DisputeDetailModal } from "./DisputeDetailModal";
import { DisputeIntakeForm } from "./DisputeIntakeForm";
import { DisputeEmptyState } from "./DisputeEmptyState";

function DisputesPageContent() {
  const location = useLocation();
  const { disputes, loading } = useDisputes();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedDispute, setSelectedDispute] = useState<DisputeDto | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showIntakeForm, setShowIntakeForm] = useState(false);

  useEffect(() => {
    const state = location.state as { selectedDisputeId?: string } | null;
    if (state?.selectedDisputeId && disputes.length > 0) {
      const dispute = disputes.find((d) => d.id === state.selectedDisputeId);
      if (dispute) {
        setSelectedDispute(dispute);
        setShowDetailModal(true);
        window.history.replaceState({}, "");
      }
    }
  }, [location.state, disputes]);

  const filteredDisputes = useMemo(() => {
    return disputes.filter((dispute) => {
      const matchesSearch =
        dispute.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === "all" || dispute.status === statusFilter;
      const matchesCategory = categoryFilter === "all" || dispute.category === categoryFilter;

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [disputes, searchQuery, statusFilter, categoryFilter]);

  const handleViewDetails = (dispute: DisputeDto) => {
    setSelectedDispute(dispute);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">Disputes</h1>
        <p className="text-muted-foreground">Manage and resolve project disputes</p>
      </div>

      <DisputeStats disputes={disputes} />

      <DisputeFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        onInitiateDispute={() => setShowIntakeForm(true)}
      />

      {filteredDisputes.length === 0 ? (
        <DisputeEmptyState
          hasDisputes={disputes.length > 0}
          onCreateDispute={() => setShowIntakeForm(true)}
        />
      ) : (
        <DisputeTable disputes={filteredDisputes} onViewDetails={handleViewDetails} />
      )}

      <DisputeDetailModal
        dispute={selectedDispute}
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
      />

      <DisputeIntakeForm open={showIntakeForm} onOpenChange={setShowIntakeForm} />
    </motion.div>
  );
}

export function DisputesPage() {
  return (
    <DisputeProvider>
      <DisputesPageContent />
    </DisputeProvider>
  );
}

